import { useTranslation } from 'react-i18next'
import Chip from '@mui/material/Chip'
import PromoCodeIcon from '@mui/icons-material/LocalOffer'

import { type Amount, Currency } from '../../../common/amount'
import { type DataOptions, useData } from '../../graphQl'
import { type Quote } from '../../quotes'
import * as queries from './promoCodes.queries'
import { type PromoCode } from './promoCodes.models'
import { applyPromoCode } from './promoCodes.utils'
import { PromoCodeType } from './promoCodes.enums'
import Price from '../../../components/Price'
import { mergeClassName } from '../../../utils/mergeClassName'

/**
 * fetch promo details
 */
export const usePromoCode = (promoCode?: string, options?: DataOptions) => {
  return useData<PromoCode | undefined>(queries.getPromoCode, {
    ...options,
    skip: !promoCode || options?.skip,
    variables: { code: promoCode },
  })
}

/**
 * apply a promo code to a quotes and returns the rebate
 * returns undefined if the rebate is zero
 */
export const usePromoCodeRebate = (quote?: Quote, promoCode?: PromoCode): Amount | undefined => {
  const updatedSubtotal = useQuoteSubtotalWithPromoCode(quote, promoCode)
  if (!quote || !updatedSubtotal) {
    return
  }
  const rebate = quote.subtotal.price - updatedSubtotal.price

  if (rebate === 0) {
    return
  }

  return {
    ...quote.subtotal,
    price: rebate * -1,
  }
}

/**
 * apply a promo code to a quote and returns the new subtotal
 */
export const useQuoteSubtotalWithPromoCode = (quote?: Quote, promoCode?: PromoCode): Amount | undefined => {
  if (!quote) {
    return
  }
  if (!promoCode) {
    return quote.subtotal
  }
  return applyPromoCode(quote.subtotal, promoCode)
}

type PromoCodeOptions = {
  standalone?: boolean
  currency?: Currency
}

/**
 * returns a tag with the label of the promo code
 */
export const useGetPromoCodeLabel = () => {
  const { t } = useTranslation()

  // eslint-disable-next-line react/display-name
  return (promoCode?: PromoCode, {
    standalone = false,
    currency = Currency.CAD,
  }: PromoCodeOptions = {}) => {
    const render = () => {
      if (!promoCode) {
        return null
      }
      return (
        <div className={mergeClassName(
          'flex items-center gap-2 font-body2 text-base font-bold',
          !standalone && 'mx-1',
        )}
        >
          { promoCode.type === PromoCodeType.Amount && (
            <Price
              amount={{ price: promoCode.value, currency }}
              showDecimals={false}
              compact
            />
          ) }
          { promoCode.type === PromoCodeType.Percentage && (
            <>
              { promoCode.value }%
            </>
          ) }
          { ' ' }
          <span className={mergeClassName(
            'text-xs',
            !standalone && 'text-gray-300',
          )}
          >
            { t('promoCode.off') }
          </span>
        </div>
      )
    }

    if (!promoCode) {
      return null
    }
    if (standalone) {
      return render()
    }
    return (
      <Chip
        className={mergeClassName(
          '!bg-green-700',
          promoCode.type === PromoCodeType.Percentage && '!bg-lime-600',
        )}
        color="success"
        icon={<PromoCodeIcon className="!ml-3 !text-[14px]" />}
        label={render()}
      />
    )
  }
}

/**
 * returns the label of the promo code as a string
 * here we hardcode the currency to $ since we might not know the currency if pre-applied
 */
export const usePromoCodeDescription = (promoCode?: PromoCode) => {
  const { t } = useTranslation()
  if (!promoCode) {
    return null
  }
  switch (promoCode.type) {
    case PromoCodeType.Amount:
      return `$${promoCode.value} ${t('promoCode.off')}`
    case PromoCodeType.Percentage:
      return `${promoCode.value}% ${t('promoCode.off')}`
    default:
      return null
  }
}
